import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import './MemberHome.css';

/* The screen behind the "Pulses" tile: the admin's own pulses, and what their people's pulses
   add up to.

   ★ The company results are TEXT only and threshold-gated. Numbers stay with the jury, and
   below the threshold there is no feedback at all — a handful of answers about a small team can
   point at one person, and no amount of careful wording fixes that. */

const CompanyPulses = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    apiRequest('GET', '/bravos/pulses')
      .then(setData)
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  const copyLink = (key, link, message) => {
    const text = `${message}\n${link}`;
    const done = () => { setCopied(key); setTimeout(() => setCopied(null), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      done();
    }
  };

  const summary = data && data.company_pulse;

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="list-check" theme="primary" className="me-2" />
          {t('yourPulses')}
        </Title>

        {data === null ? (
          <span className="auth__spinner" />
        ) : (
          <>
            <p className="bravos-q__hint mb-2">{t('yourPulsesNote')}</p>
            {(data.ctas || []).map((c) => (
              <div key={c.key} className="bravos-cta-row">
                <button
                  type="button"
                  className="bravos-cta"
                  onClick={() => navigate(`/bravos/survey/${c.key}`)}
                >
                  <span className="bravos-cta__name">{c.name}</span>
                  {!c.available ? (
                    <span className="bravos-cta__tag">{t('comingSoon')}</span>
                  ) : c.completed ? (
                    <span className="bravos-cta__tag bravos-cta__tag--done">{t('seeResults')}</span>
                  ) : (
                    <span className="bravos-cta__tag bravos-cta__tag--do">{t('doTest')}</span>
                  )}
                  <Icon name="angle-right" className="bravos-event__chev" />
                </button>
                {c.share_link && (
                  <button
                    type="button"
                    className="bravos-copy bravos-cta__share"
                    title={t('sendTestHint')}
                    onClick={() => copyLink(`pulse-${c.key}`, c.share_link,
                      t('pulseShareText', { name: c.name }))}
                  >
                    <Icon name={copied === `pulse-${c.key}` ? 'check' : 'paper-plane'} />
                    {copied === `pulse-${c.key}` ? t('copied') : t('sendTest')}
                  </button>
                )}
              </div>
            ))}
          </>
        )}
      </Card>

      {summary && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="chart-histogram" theme="primary" className="me-2" />
            {t('companyPulseTitle')}
          </Title>
          {summary.below_threshold ? (
            <p className="bravos-q__hint mb-2">{t('companyPulseNeedData')}</p>
          ) : (
            <>
              <p className="bravos-q__hint mb-2">{t('companyPulseNote')}</p>
              {(summary.instruments || []).map((inst) => (
                <div key={inst.key} className="mb-2">
                  <p className="bravos-cal__label">{inst.name}</p>
                  {inst.blocks.map((b, i) => (
                    <div key={`${inst.key}-b${i}`} className="bravos-fb-block">
                      {b.bucket && b.bucket !== 'OVERALL'
                        && <p className="bravos-fb-sub">{b.bucket}</p>}
                      <p className="bravos-fb-block__text">{b.feedback}</p>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </Card>
      )}
    </Base>
  );
};

export default CompanyPulses;
